import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BottomNav from '../shared/BottomNav.jsx';
import Navbar from '../shared/Navbar.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './Home.css';

/* ─── DATA ────────────────────────────────────────────────────── */
const VX_CITIES = ['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan', 'Safi', 'El Jadida', 'Béni Mellal', 'Nador', 'Taza', 'Khouribga', 'Settat', 'Larache', 'Ksar El Kebir', 'Guelmim', 'Laâyoune', 'Dakhla', 'Errachidia', 'Ouarzazate', 'Taroudant'];


const VX_STATS = [
  { value: '3 800+', label: 'Véhicules actifs' },
  { value: '142', label: 'Agences certifiées' },
  { value: '98K+', label: 'Réservations' },
  { value: '4.9', label: 'Note moyenne' },
];

const VX_STEPS = [
  {
    num: '01',
    title: 'Exprimez votre besoin',
    text: 'Indiquez destination, nombre de passagers, budget et style de voyage pour une recherche véritablement pertinente.',
  },
  {
    num: '02',
    title: 'Recommandation IA',
    text: 'Notre moteur analyse votre contexte et propose le véhicule le plus adapté avant même la comparaison détaillée.',
  },
  {
    num: '03',
    title: 'Réservation sereine',
    text: 'Validez parmi des véhicules certifiés qui garantissent sérieux, sécurité totale et transparence tarifaire.',
  },
];

const VX_FEATURES = [
  { num: '01', title: 'Tarifs comparés en temps réel', text: 'Le moteur compare les prix en direct et met immédiatement en avant les offres les plus pertinentes pour votre recherche.', tag: 'Live' },
  { num: '02', title: 'Première plateforme IA de location', text: 'UltimateXDrive transforme une demande naturelle en recommandation de location immédiate grâce à une IA entraînée sur vos usages.', tag: 'Smart' },
  { num: '03', title: 'Collaborateurs certifiés uniquement', text: 'Toutes les agences visibles sur la plateforme sont contrôlées pour leur fiabilité, leur niveau de sécurité et leur réactivité.', tag: 'Trust' },
  { num: '04', title: 'Sécurité & sérénité incluses', text: 'Paiement sécurisé, conditions lisibles, assistance 24h/24 et 7J/7 et garantie de confiance du début à la fin du parcours.', tag: 'Global' },
];

const VX_SERVICES = [
  { num: '01', title: 'Annulation flexible', text: 'Modifiez vos plans en quelques secondes sans frais cachés ni délais d\'attente excessifs.' },
  { num: '02', title: 'Paiement sécurisé', text: 'Transactions chiffrées, vérification des partenaires et parcours conçu pour inspirer confiance.' },
  { num: '03', title: 'Assistance continue', text: 'Une aide disponible avant, pendant et après le retrait du véhicule pour éviter toute friction.' },
  { num: '04', title: 'Options à la carte', text: 'Siège bébé, GPS, assurance, conducteur additionnel et services pratiques accessibles en un clic.' },
];


const VX_REVIEWS = [
  { name: 'Driss A.', role: 'Head of Sales', text: 'Enfin une plateforme qui ressemble à un vrai produit premium. Tout inspire confiance, de la recherche au paiement.', score: '5.0/5', initials: 'CN' },
  { name: 'Riyad S.', role: 'Freelance', text: 'Le niveau visuel est excellent et les conditions sont beaucoup plus faciles à comprendre que chez la concurrence.', score: '4.9/5', initials: 'YD' },
  { name: 'Othmane M.', role: 'Product Manager', text: 'L\'ambiance épurée et la fluidité donnent immédiatement l\'impression d\'une application sérieuse et haut de gamme.', score: '4.8/5', initials: 'LF' },
];

const VX_MAP_CITIES = [
  { city: 'Casablanca', x: 36, y: 66, available: 142, status: 'Hot' },
  { city: 'Rabat', x: 41, y: 61, available: 87, status: 'Actif' },
  { city: 'Marrakech', x: 47, y: 73, available: 63, status: 'Hot' },
  { city: 'Fès', x: 53, y: 56, available: 49, status: 'Actif' },
  { city: 'Tanger', x: 46, y: 46, available: 38, status: 'Actif' },
  { city: 'Agadir', x: 39, y: 84, available: 29, status: 'Calme' },
];

function getStoredAuthUser() {
  try {
    const raw = window.localStorage.getItem('exp-auth-user');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

/* ─── LOADER ─────────────────────────────────────────────────── */
function VXLoader() {
  return (
    <div className="vx-loader" aria-hidden="true">
      <div className="vx-loader-wordmark">UltimateXDrive</div>
      <div className="vx-loader-line" />
      <div className="vx-loader-pct">CHARGEMENT</div>
    </div>
  );
}

/* ─── HERO ───────────────────────────────────────────────────── */
function VXHero({ authUser, onSignup }) {
  const displayName = authUser?.firstName || authUser?.prenom || authUser?.lastName || authUser?.nom || '';

  return (
    <section className="vx-hero">
      <div className="vx-hero-bg">
        <img
          src="/assets/images/interface.png"
          alt=""
          aria-hidden="true"
        />
      </div>

      <div className="vx-shell" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', position: 'relative', zIndex: 2, paddingTop: '8rem', paddingBottom: '4rem' }}>
        {authUser ? (
          <span className="vx-hero-user-pill">
            <span className="vx-hero-eyebrow-dot" />
            Bienvenue, {displayName || 'utilisateur'}
          </span>
        ) : (
          <span className="vx-hero-eyebrow">
            <span className="vx-hero-eyebrow-dot" />
            Plateforme multi-agences — IA intégrée — Disponible 24h/24 et 7J/7
          </span>
        )}

        <h1>
          Louez<br />
          <em>autrement.</em>
        </h1>

        <div className="vx-hero-stats">
          <div className="vx-hero-stat">
            <strong>3 800+</strong>
            <span>Véhicules actifs</span>
          </div>
          <div className="vx-hero-stat">
            <strong>142</strong>
            <span>Agences certifiées</span>
          </div>
          <div className="vx-hero-stat">
            <strong>4.9/5</strong>
            <span>Note moyenne</span>
          </div>
        </div>
      </div>

      <div className="vx-hero-bottom" style={{ position: 'relative', zIndex: 2 }}>
        <div className="vx-shell" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'end', gap: '2rem', width: '100%' }}>
          <p className="vx-hero-desc">
            Une plateforme premium qui compare en temps réel les meilleures agences de location autour de vous. Recommandation IA, paiement sécurisé, expérience sans friction.
          </p>
          <div className="vx-hero-cta">
            {authUser ? (
              <Link to="/profile" className="vx-btn vx-btn-solid">Mon profil</Link>
            ) : (
              <Link to="/search" className="vx-btn vx-btn-solid">Démarrer →</Link>
            )}
            <a href="/assistant-ia" className="vx-btn vx-btn-outline-white">Assistant IA</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── TICKER ─────────────────────────────────────────────────── */
function VXTicker() {
  const items = [...VX_CITIES, ...VX_CITIES];
  return (
    <div className="vx-ticker" aria-hidden="true">
      <div className="vx-ticker-track">
        {items.map((p, i) => (
          <span key={i} className="vx-ticker-item">
            <b>{p}</b>
            <span className="vx-ticker-sep" />
          </span>
        ))}
        {items.map((p, i) => (
          <span key={`dup-${i}`} className="vx-ticker-item">
            <b>{p}</b>
            <span className="vx-ticker-sep" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── SEARCH ─────────────────────────────────────────────────── */
function VXSearch() {
  const [tab, setTab] = useState('location');
  const navigate = useNavigate();
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [searchData, setSearchData] = useState({ ville: '', recherche: '', dateDebut: '', dateFin: '', budgetMax: '', typeVehicule: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate('/search');
  };

  const handleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (!recognitionRef.current) {
      const r = new SR();
      r.lang = 'fr-FR';
      r.onresult = (ev) => {
        const t = Array.from(ev.results).map(res => res[0].transcript).join(' ').trim();
        setSearchData(p => ({ ...p, recherche: t }));
      };
      r.onend = () => setListening(false);
      recognitionRef.current = r;
    }
    if (listening) { recognitionRef.current.stop(); } else { recognitionRef.current.start(); setListening(true); }
  };

  return (
    <section className="vx-search-wrap" id="recherche">
      <div className="vx-shell vx-search-container">

        {/* ── Left: marketing content ── */}
        <div className="vx-search-left">
          <span className="vx-search-eyebrow">
            <span className="vx-hero-eyebrow-dot" />
            IA · Recommandation intelligente
          </span>
          <h2 className="vx-search-heading">
            Trouvez la voiture qui colle vraiment à votre trajet
          </h2>
          <p className="vx-search-desc">
            Notre IA analyse votre besoin et recommande le véhicule idéal avant même que vous ne compariez.
          </p>
          <div className="vx-search-features">
            {[
              'Réservation instantanée en 2 minutes',
              'Prix transparents sans frais cachés',
              'Agences vérifiées et notées',
            ].map(f => (
              <span key={f} className="vx-search-feature">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/><polyline points="4,7.2 6.2,9.2 10,5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinejoin="round"/></svg>
                {f}
              </span>
            ))}
          </div>
          <div className="vx-search-ai-hint">
            <span className="vx-ai-powered-dot" />
            L'IA peut suggérer le véhicule idéal — essayez l'assistant ci-contre
          </div>
        </div>

        {/* ── Right: search card ── */}
        <div className="vx-search-card">
          <div className="vx-search-tabs">
            {['location', 'chauffeur', 'aéroport'].map(t => (
              <button key={t} className={`vx-chip${tab === t ? ' is-active' : ''}`} onClick={() => setTab(t)}>
                {t}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="vx-search-rows">
              <div className="vx-search-row vx-search-row-2">
                <div className="vx-search-field">
                  <span className="vx-search-label">Ville</span>
                  <input name="ville" type="text" value={searchData.ville} onChange={handleChange} placeholder="Casablanca, Rabat..." />
                </div>
                <div className="vx-search-field">
                  <span className="vx-search-label">Recherche</span>
                  <div className="vx-search-field-inner">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2"/><line x1="7.5" y1="7.5" x2="11" y2="11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                    <input name="recherche" type="text" value={searchData.recherche} onChange={handleChange} placeholder="Clio, SUV, chauffeur..." />
                  </div>
                </div>
              </div>
              <div className="vx-search-row vx-search-row-3">
                <div className="vx-search-field">
                  <span className="vx-search-label">Date début</span>
                  <input name="dateDebut" type="date" value={searchData.dateDebut} onChange={handleChange} />
                </div>
                <div className="vx-search-field">
                  <span className="vx-search-label">Date fin</span>
                  <input name="dateFin" type="date" value={searchData.dateFin} onChange={handleChange} />
                </div>
                <div className="vx-search-field">
                  <span className="vx-search-label">Budget max</span>
                  <div className="vx-search-field-inner">
                    <input name="budgetMax" type="number" value={searchData.budgetMax} onChange={handleChange} placeholder="500" min="30" max="5000" />
                    <span className="vx-search-suffix">DH/jour</span>
                  </div>
                </div>
              </div>
              <div className="vx-search-row vx-search-row-1">
                <div className="vx-search-field">
                  <span className="vx-search-label">Type de véhicule</span>
                  <select name="typeVehicule" value={searchData.typeVehicule} onChange={handleChange}>
                    <option value="">Tous</option>
                    <option value="citadine">Citadine</option>
                    <option value="suv">SUV</option>
                    <option value="berline">Berline</option>
                    <option value="electrique">Électrique</option>
                    <option value="van">Van</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vx-search-actions">
              <button type="submit" className="vx-search-submit">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/><line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Rechercher
              </button>
              <button type="button" className={`vx-search-btn-sec${listening ? ' is-active' : ''}`} onClick={handleVoice}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><rect x="5" y="1" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M2 7C2 10 12 10 12 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><line x1="7" y1="10" x2="7" y2="13" stroke="currentColor" strokeWidth="1.3"/></svg>
                {listening ? 'Écoute...' : 'Micro'}
              </button>
              <a href="/assistant-ia" className="vx-search-btn-sec">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 1L8.35 5H13L9.3 7.7L10.6 12L7 9.5L3.4 12L4.7 7.7L1 5H5.65L7 1Z" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>
                Assistant IA
              </a>
            </div>
          </form>
        </div>

      </div>
    </section>
  );
}

/* ─── STATS ──────────────────────────────────────────────────── */
function VXStats() {
  return (
    <section className="vx-stats vx-reveal">
      {VX_STATS.map(s => (
        <div key={s.label} className="vx-stat">
          <span className="vx-stat-num">{s.value}</span>
          <span className="vx-stat-label">{s.label}</span>
        </div>
      ))}
    </section>
  );
}

/* ─── LIVE MAP ───────────────────────────────────────────────── */
function VXLiveMap() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeconds(p => (p + 1) % 60), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="vx-section">
      <div className="vx-shell">
        <div className="vx-section-head vx-reveal">
          <div>
            <span className="vx-section-kicker">Disponibilité temps réel</span>
            <h2 className="vx-section-title">Disponibilité en direct</h2>
          </div>
        </div>

        <div className="vx-live-grid vx-reveal">
          <div className="vx-live-panel">
            <div className="vx-live-header">
              <span className="vx-live-dot" />
              Mis à jour il y a {seconds}s
            </div>
            <div className="vx-city-list">
              {VX_MAP_CITIES.map(c => (
                <div key={c.city} className="vx-city-row">
                  <strong>{c.city}</strong>
                  <span>{c.available} véh.</span>
                  <span className={`vx-city-badge${c.status === 'Hot' ? ' is-hot' : ''}`}>{c.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="vx-map-container">
            <img src="/assets/images/maroc.png" alt="Carte du Maroc" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── CARS ───────────────────────────────────────────────────── */
const API_BASE = getApiBaseUrl();

function StarRating({ note }) {
  const score = Math.round(Math.max(0, Math.min(5, Number(note) || 0)));
  return (
    <span className="vx-car-stars" aria-label={`${score} étoiles sur 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < score ? 'vx-star filled' : 'vx-star'}>★</span>
      ))}
    </span>
  );
}

function VXCars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/products`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        if (cancelled) return;
        const available = Array.isArray(data)
          ? data.filter((p) => p.isAvailable !== false)
          : [];
        const top = available
          .sort((a, b) => Number(b.note || 0) - Number(a.note || 0) || Number(b.price || 0) - Number(a.price || 0))
          .slice(0, 6);
        setCars(top);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const buildPlaceholder = (name) => {
    const initials = (name || 'V').slice(0, 2).toUpperCase();
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240'><rect width='400' height='240' fill='%231a1a1a'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='48' fill='%23333'>${initials}</text></svg>`;
    return `data:image/svg+xml,${svg}`;
  };

  if (loading) {
    return (
      <section className="vx-section vx-section-alt" id="flotte">
        <div className="vx-shell">
          <div className="vx-cars-loading">Chargement des véhicules…</div>
        </div>
      </section>
    );
  }

  return (
    <section className="vx-section vx-section-alt" id="flotte">
      <div className="vx-shell">
        <div className="vx-section-head">
          <div>
            <span className="vx-section-kicker">Sélection premium</span>
            <h2 className="vx-section-title">Véhicules d'exception</h2>
          </div>
          <Link to="/search" className="vx-link-arrow">
            Voir toute la flotte
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="2" y1="7" x2="12" y2="7" stroke="currentColor" strokeWidth="1.5"/><polyline points="8,3 12,7 8,11" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/></svg>
          </Link>
        </div>

        {!cars.length ? (
          <p className="vx-cars-empty">Aucun véhicule disponible pour le moment. Revenez bientôt !</p>
        ) : (
        <div className="vx-cars-grid">
          {cars.map((car) => {
            const name = car.name || [car.vehicleBrand, car.vehicleModel].filter(Boolean).join(' ') || 'Véhicule';
            const image = car.mainImageUrl || (car.galleryImages || [])[0] || buildPlaceholder(name);
            const note = Number(car.note || 0);
            return (
              <article key={car._id} className="vx-car-card">
                <div className="vx-car-img-wrap">
                  <img src={image} alt={name} loading="lazy" />
                  {note >= 4.5 && <span className="vx-car-badge">Top noté</span>}
                  {note >= 4 && note < 4.5 && <span className="vx-car-badge" style={{ background: '#16a34a' }}>Très bien</span>}
                </div>
                <div className="vx-car-body">
                  <span className="vx-car-agency">{car.agencyName || 'Agence'} — {car.city || ''}</span>
                  <span className="vx-car-name">{name}</span>
                  {note > 0 && (
                    <div className="vx-car-rating-row">
                      <StarRating note={note} />
                      <span className="vx-car-rating-val">{note.toFixed(1)}</span>
                    </div>
                  )}
                  <div className="vx-car-tags">
                    {car.seats && <span className="vx-car-tag">{car.seats} pl.</span>}
                    {car.transmission && <span className="vx-car-tag">{car.transmission}</span>}
                    {car.fuelType && <span className="vx-car-tag">{car.fuelType}</span>}
                    {car.vehicleYear && <span className="vx-car-tag">{car.vehicleYear}</span>}
                  </div>
                  <div className="vx-car-footer">
                    <span className="vx-car-price">{Number(car.price || 0).toLocaleString('fr-FR')} <sub>MAD / jr</sub></span>
                    <Link to={`/vehicle/${car._id}`} className="vx-link-arrow">
                      Réserver
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5"/><polyline points="7,3 10,6 7,9" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
}

/* ─── STEPS ──────────────────────────────────────────────────── */
function VXSteps() {
  return (
    <section className="vx-section">
      <div className="vx-shell">
        <div className="vx-section-head vx-reveal">
          <div>
            <span className="vx-section-kicker">Comment ça marche</span>
            <h2 className="vx-section-title">Simple. Rapide. Fiable.</h2>
          </div>
        </div>
        <div className="vx-steps vx-reveal">
          {VX_STEPS.map(s => (
            <div key={s.num} className="vx-step">
              <span className="vx-step-num">{s.num}</span>
              <div className="vx-step-title">{s.title}</div>
              <p className="vx-step-text">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FEATURES ───────────────────────────────────────────────── */
function VXFeatures() {
  return (
    <section className="vx-section vx-section-alt">
      <div className="vx-shell">
        <div className="vx-section-head vx-reveal">
          <div>
            <span className="vx-section-kicker">Notre différence</span>
            <h2 className="vx-section-title">Conçu pour les exigeants</h2>
          </div>
        </div>
        <div className="vx-features-grid vx-reveal">
          {VX_FEATURES.map(f => (
            <div key={f.num} className="vx-feature">
              <span className="vx-feature-num">{f.num}</span>
              <div className="vx-feature-title">{f.title}</div>
              <p className="vx-feature-text">{f.text}</p>
              <span className="vx-feature-tag">{f.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── AI ASSISTANT ───────────────────────────────────────────── */
function VXAiAssistant() {
  return (
    <section className="vx-section vx-section-dark vx-ai-section" id="assistant">
      <div className="vx-ai-orb" aria-hidden="true" />
      <div className="vx-ai-orb vx-ai-orb-2" aria-hidden="true" />

      <div className="vx-shell">
        <div className="vx-ai-hero vx-reveal">
          <div className="vx-ai-powered-chip">
            <span className="vx-ai-powered-dot" />
            Powered by AI
          </div>
          <h2 className="vx-ai-hero-title">
            Dites-nous vos besoins.<br />
            Notre IA vous trouve<br />
            <em className="vx-ai-hero-em">la voiture idéale.</em>
          </h2>
          <p className="vx-ai-hero-sub">
            Un seul clic suffit pour lancer un assistant qui comprend votre trajet et propose la meilleure option de location.
          </p>
        </div>

        <div className="vx-ai-grid vx-ai-grid-simple vx-reveal">
          <div className="vx-ai-left vx-ai-left-simple">
            <div className="vx-ai-left-header">
              <span className="vx-ai-panel-kicker">Exemple concret</span>
            </div>
            <p className="vx-ai-simple-text">
              Exemple: <strong>"Je suis à Casablanca, 2 personnes, budget 350 MAD/jour, trajet urbain"</strong>. L’IA répond directement avec un modèle recommandé, une raison claire et un tarif estimé.
            </p>
            <Link to="/assistant-ia" className="vx-ai-start-btn">
              Commencer avec l’IA
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="2" y1="7" x2="12" y2="7" stroke="currentColor" strokeWidth="1.5"/><polyline points="8,3 12,7 8,11" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/></svg>
            </Link>
          </div>

          <div className="vx-ai-right vx-ai-right-simple">
            <div className="vx-ai-right-header">
              <span className="vx-ai-panel-kicker">Réponse IA</span>
              <span className="vx-ai-active-badge">
                <span className="vx-ai-powered-dot" style={{ width: 5, height: 5 }} />
                Prêt
              </span>
            </div>
            <div className="vx-ai-result is-active">
              <span className="vx-ai-result-label">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M6 0L7.35 4.35H12L8.4 7.05L9.6 11.7L6 9L2.4 11.7L3.6 7.05L0 4.35H4.65L6 0Z"/></svg>
                Recommandation IA
              </span>
              <div className="vx-ai-result-model">Renault Clio</div>
              <div className="vx-ai-result-type">Citadine</div>
              <p className="vx-ai-result-reason">Idéale pour un usage urbain, économique en carburant, facile à stationner et adaptée à un budget modéré.</p>
              <div className="vx-ai-result-footer">
                <span className="vx-ai-result-price">À partir de 460 MAD / jour</span>
                <Link to="/search" className="vx-ai-result-cta">Voir les offres →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── SERVICES ───────────────────────────────────────────────── */
function VXServices() {
  return (
    <section className="vx-section" id="services">
      <div className="vx-shell">
        <div className="vx-section-head vx-reveal">
          <div>
            <span className="vx-section-kicker">Inclus dans chaque réservation</span>
            <h2 className="vx-section-title">Services premium de série</h2>
          </div>
        </div>
        <div className="vx-services vx-reveal">
          {VX_SERVICES.map(s => (
            <div key={s.num} className="vx-service">
              <span className="vx-service-num">{s.num}</span>
              <div className="vx-service-title">{s.title}</div>
              <p className="vx-service-text">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── REVIEWS ────────────────────────────────────────────────── */
function VXReviews() {
  return (
    <section className="vx-section vx-section-alt">
      <div className="vx-shell">
        <div className="vx-section-head vx-reveal">
          <div>
            <span className="vx-section-kicker">Ils nous font confiance</span>
            <h2 className="vx-section-title">Ce qu'ils en disent</h2>
          </div>
        </div>
        <div className="vx-reviews-grid vx-reveal">
          {VX_REVIEWS.map(r => (
            <article key={r.name} className="vx-review">
              <div className="vx-review-head">
                <div>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                    <div className="vx-review-avatar">{r.initials}</div>
                    <div>
                      <span className="vx-review-name">{r.name}</span>
                      <span className="vx-review-role">{r.role}</span>
                    </div>
                  </div>
                </div>
                <span className="vx-review-score">{r.score}</span>
              </div>
              <p className="vx-review-text">"{r.text}"</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA BAND ───────────────────────────────────────────────── */
function VXCtaBand({ authUser, onSignup }) {
  return (
    <section style={{ background: '#000', padding: '0' }}>
      <div className="vx-shell">
        <div className="vx-cta-band vx-reveal">
          <h2>{authUser ? <>Ravi de vous revoir, <em>{authUser.firstName || authUser.prenom || 'conducteur'}</em> ?</> : <>Prêt à conduire <em>autrement</em> ?</>}</h2>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
            {authUser ? (
              <Link to="/profile" className="vx-btn vx-btn-solid" style={{ background: '#fff', color: '#000', borderColor: '#fff' }}>
                Accéder à mon profil
              </Link>
            ) : (
              <button className="vx-btn vx-btn-solid" onClick={onSignup} style={{ background: '#fff', color: '#000', borderColor: '#fff' }}>
                Créer un compte →
              </button>
            )}
            <Link to="/search" className="vx-btn vx-btn-outline-white">Rechercher</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─────────────────────────────────────────────────── */
function VXFooter() {
  return (
    <footer className="vx-footer" id="contact">
      <div className="vx-shell">
        <div className="vx-footer-top">
          <div className="vx-footer-col">
            <span className="vx-footer-brand-name">UltimateXDrive</span>
            <p className="vx-footer-desc">Plateforme multi-agences avec UX premium, recommandation IA et expérience internationale sans compromis.</p>
          </div>
          <div className="vx-footer-col">
            <span className="vx-footer-heading">Navigation</span>
            <ul className="vx-footer-links">
              <li><Link to="/search" className="vx-footer-link-btn">Recherche</Link></li>
              <li><Link to="/#flotte" className="vx-footer-link-btn">Flotte</Link></li>
              <li><Link to="/about" className="vx-footer-link-btn">À propos</Link></li>
              <li><Link to="/contact" className="vx-footer-link-btn">Contact & support</Link></li>
              <li><Link to="/assistant-ia" className="vx-footer-link-btn">Assistant IA</Link></li>
            </ul>
          </div>
          <div className="vx-footer-col">
            <span className="vx-footer-heading">Contact</span>
            <ul className="vx-footer-links">
              <li><span>ultimatexdrive@gmail.com</span></li>
              <li><span>+212 5 20 100 220</span></li>
              <li><span>Casablanca Finance City</span></li>
            </ul>
          </div>
          <div className="vx-footer-col">
            <span className="vx-footer-heading">Réseaux</span>
            <ul className="vx-footer-links">
              {['Instagram', 'LinkedIn', 'X / Twitter'].map(s => (
                <li key={s}><button className="vx-footer-link-btn" onClick={(e) => e.preventDefault()}>{s}</button></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="vx-footer-bottom">
          <span className="vx-footer-copy">© {new Date().getFullYear()} UltimateXDrive. Tous droits réservés.</span>
          <span className="vx-footer-copy">Conçu & développé avec exigence.</span>
        </div>
      </div>
    </footer>
  );
}

/* ─── COOKIE BANNER ──────────────────────────────────────────── */
function VXCookie({ onAccept, onRefuse }) {
  return (
    <div className="vx-cookie" role="dialog" aria-label="Gestion des cookies">
      <p>Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic. Vous pouvez accepter ou refuser leur utilisation.</p>
      <div className="vx-cookie-btns">
        <button className="vx-btn vx-btn-outline-white" onClick={onRefuse}>Refuser</button>
        <button className="vx-btn" onClick={onAccept} style={{ background: '#fff', color: '#000', borderColor: '#fff' }}>Accepter</button>
      </div>
    </div>
  );
}

/* ─── SCROLL REVEAL HOOK ─────────────────────────────────────── */
function useReveal(ready) {
  useEffect(() => {
    if (!ready) return;
    const nodes = document.querySelectorAll('.vx-reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vx-visible'); }),
      { threshold: 0.12 }
    );
    nodes.forEach(n => obs.observe(n));
    return () => { nodes.forEach(n => obs.unobserve(n)); obs.disconnect(); };
  }, [ready]);
}

/* ─── HOME PAGE ──────────────────────────────────────────────── */
function Home() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cookieVisible, setCookieVisible] = useState(false);
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());

  useReveal(!loading);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!window.localStorage.getItem('vx-cookie')) setCookieVisible(true);
  }, []);

  useEffect(() => {
    const handleStorage = () => setAuthUser(getStoredAuthUser());

    window.addEventListener('storage', handleStorage);
    handleStorage();

    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const acceptCookie = () => { window.localStorage.setItem('vx-cookie', '1'); setCookieVisible(false); };
  const refuseCookie = () => { window.localStorage.setItem('vx-cookie', '0'); setCookieVisible(false); };

  if (loading) return <VXLoader />;

  return (
    <div className="vx-page">
      <div className="vx-noise" aria-hidden="true" />

      <Navbar />

      <main>
        <VXHero authUser={authUser} onSignup={() => navigate('/signup')} />
        <VXTicker />
        <VXSearch />
        <VXStats />
        <VXLiveMap />
        <VXCars />
        <VXSteps />
        <VXFeatures />
        <VXAiAssistant />
        <VXServices />
        <VXReviews />
        <VXCtaBand authUser={authUser} onSignup={() => navigate('/signup')} />
      </main>

      <VXFooter />

      {cookieVisible && <VXCookie onAccept={acceptCookie} onRefuse={refuseCookie} />}

      <BottomNav />
    </div>
  );
}

export default Home;